import { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, TrendingDown, Dog, Cat, Heart, AlertTriangle, ChevronLeft, ChevronRight, Printer, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#2563eb', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
const PIE_COLORS = ['#2563eb', '#ec4899'];
const BAR_COLORS = { adocao: '#10b981', morte: '#6b7280', eutanasia: '#ef4444', levantamento: '#3b82f6' };
const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function StatisticsPage() {
  const [stats, setStats] = useState(null);
  const [mensal, setMensal] = useState(null);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [showPrint, setShowPrint] = useState({
    geral: true, entradas: true, saidas: true, medico: true, mensal: true
  });

  const carregar = () => {
    const params = dataInicio && dataFim ? `?data_inicio=${dataInicio}&data_fim=${dataFim}` : '';
    axios.get(`${API}/estatisticas${params}`).then(r => setStats(r.data)).catch(console.error);
  };

  useEffect(() => { carregar(); }, []);
  useEffect(() => { axios.get(`${API}/estatisticas/mensal?ano=${ano}&mes=${mes}`).then(r => setMensal(r.data)).catch(console.error); }, [ano, mes]);

  const aplicarFiltro = () => { carregar(); };
  const imprimir = () => window.print();

  if (!stats) return <div className="text-center py-12 text-muted-foreground">A carregar...</div>;

  const pieData = [
    { name: 'Cães', value: stats.caes_ativos },
    { name: 'Gatos', value: stats.gatos_ativos },
  ].filter(d => d.value > 0);

  const saidasData = [
    { name: 'Adoções Cães', value: stats.saidas?.adocoes_caes || 0, fill: '#10b981' },
    { name: 'Adoções Gatos', value: stats.saidas?.adocoes_gatos || 0, fill: '#34d399' },
    { name: 'M.Nat. Cães', value: stats.saidas?.mortes_naturais_caes || 0, fill: '#6b7280' },
    { name: 'M.Nat. Gatos', value: stats.saidas?.mortes_naturais_gatos || 0, fill: '#9ca3af' },
    { name: 'Eut. Cães', value: stats.saidas?.eutanasias_caes || 0, fill: '#ef4444' },
    { name: 'Eut. Gatos', value: stats.saidas?.eutanasias_gatos || 0, fill: '#f87171' },
  ].filter(d => d.value > 0);

  const entradasData = [
    { name: 'Titular', value: stats.entradas?.titular || 0, fill: '#2563eb' },
    { name: 'Achador', value: stats.entradas?.achador || 0, fill: '#3b82f6' },
    { name: 'Errante', value: stats.entradas?.captura_errante || 0, fill: '#f59e0b' },
    { name: 'Domicílio', value: stats.entradas?.captura_domicilio || 0, fill: '#f97316' },
    { name: 'Sequestro', value: stats.entradas?.sequestro || 0, fill: '#8b5cf6' },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="statistics-page">
      <div className="flex items-center justify-between mb-6 print:mb-2">
        <h2 className="font-heading font-bold text-2xl">Estatísticas</h2>
        <button onClick={imprimir} className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110 print:hidden">
          <Printer className="w-4 h-4" /> Imprimir
        </button>
        <button onClick={() => {
          const params = new URLSearchParams();
          if (dataInicio) params.set('data_inicio', dataInicio);
          if (dataFim) params.set('data_fim', dataFim);
          params.set('ano', ano);
          params.set('mes', mes);
          window.open(`${API}/estatisticas/pdf?${params}`, '_blank');
        }} data-testid="export-pdf-btn" className="flex items-center gap-1 bg-accent text-accent-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110 print:hidden">
          <BarChart3 className="w-4 h-4" /> Exportar PDF
        </button>
      </div>

      {/* Period filter */}
      <div className="bg-card rounded-2xl border border-border p-4 mb-6 print:hidden">
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="text-xs text-muted-foreground block mb-1">Data Início</label><input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
          <div><label className="text-xs text-muted-foreground block mb-1">Data Fim</label><input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
          <button onClick={aplicarFiltro} className="flex items-center gap-1 bg-accent text-accent-foreground rounded-full px-3 py-1.5 text-sm"><Filter className="w-3.5 h-3.5" />Filtrar</button>
          <button onClick={() => { setDataInicio(''); setDataFim(''); setTimeout(carregar, 100); }} className="text-xs text-muted-foreground hover:text-foreground">Limpar</button>
        </div>
        {dataInicio && dataFim && <p className="text-xs text-muted-foreground mt-2">A mostrar dados de {dataInicio} a {dataFim}</p>}
      </div>

      {/* General stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total de Animais" value={stats.total_animais ?? stats.ativos_sem_sequestro} sub="No canil (exclui sequestros domiciliários)" color="text-primary" />
        <StatCard label="Cães / Gatos" value={`${stats.caes_ativos} / ${stats.gatos_ativos}`} sub="No canil" color="text-accent" />
        <StatCard label="Adoções Total" value={stats.saidas?.adocoes_total || 0} sub={`Cães: ${stats.saidas?.adocoes_caes || 0} | Gatos: ${stats.saidas?.adocoes_gatos || 0}`} color="text-green-600" />
        <StatCard label="Levantamentos" value={stats.saidas?.levantamentos || 0} sub={`Sequestro: ${stats.saidas?.levantamentos_sequestro || 0}`} color="text-blue-600" />
      </div>

      {/* Entries */}
      <div className="bg-card rounded-2xl border border-border p-6 mb-6">
        <h3 className="font-heading font-bold text-lg mb-3">Entradas por Tipo</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MiniStat label="Entrega (titular)" value={stats.entradas?.titular || 0} />
          <MiniStat label="Entrega (achador)" value={stats.entradas?.achador || 0} />
          <MiniStat label="Captura (errante)" value={stats.entradas?.captura_errante || 0} />
          <MiniStat label="Captura (domicílio)" value={stats.entradas?.captura_domicilio || 0} />
          <MiniStat label="Sequestro CRO" value={stats.entradas?.sequestro || 0} />
          <MiniStat label="Sequestro Domiciliário" value={stats.entradas?.sequestro_domiciliario || 0} sub="Não contabiliza no canil" />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <MiniStat label="Doações (titular)" value={stats.entradas?.doacoes_titular || 0} sub="Não contabiliza como novo animal" />
          <MiniStat label="Doações (sequestro)" value={stats.entradas?.doacoes_sequestro || 0} sub="Contabiliza para estatísticas" />
        </div>
      </div>

      {/* Deaths */}
      <div className="bg-card rounded-2xl border border-border p-6 mb-6">
        <h3 className="font-heading font-bold text-lg mb-3">Falecimentos</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Morte Natural (Cães)" value={stats.saidas?.mortes_naturais_caes || 0} />
          <MiniStat label="Morte Natural (Gatos)" value={stats.saidas?.mortes_naturais_gatos || 0} />
          <MiniStat label="Eutanásia (Cães)" value={stats.saidas?.eutanasias_caes || 0} />
          <MiniStat label="Eutanásia (Gatos)" value={stats.saidas?.eutanasias_gatos || 0} />
        </div>
        <div className="mt-3">
          <MiniStat label="Eutanásia por Leishmaniose" value={stats.saidas?.eutanasias_leishmaniose || 0} />
        </div>
      </div>

      {/* Medical */}
      <div className="bg-card rounded-2xl border border-border p-6 mb-6">
        <h3 className="font-heading font-bold text-lg mb-3">Dados Médicos</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Testados Leishmaniose" value={stats.medico?.testados_leishmaniose || 0} />
          <MiniStat label="Positivos Leishmaniose" value={stats.medico?.positivos_leishmaniose || 0} />
          <MiniStat label="Tratados Leishmaniose" value={stats.medico?.tratados_leishmaniose || 0} />
          <MiniStat label="Eutanás. Leishmaniose" value={stats.medico?.eutanasiados_leishmaniose || 0} />
        </div>
      </div>

      {/* Tratamentos Externos */}
      <div className="bg-card rounded-2xl border border-border p-6 mb-6">
        <h3 className="font-heading font-bold text-lg mb-3">Tratamentos Externos</h3>
        <p className="text-xs text-muted-foreground mb-3">Animais que vêm ao CRO apenas para tratamento (não entram no canil).</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="No período selecionado" value={stats.tratamentos_externos?.periodo || 0} />
          <MiniStat label="Total geral" value={stats.tratamentos_externos?.total || 0} />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading font-bold text-lg mb-4">Animais por Espécie</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={5} dataKey="value">{pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}</Pie><Legend /><Tooltip /></PieChart>
            </ResponsiveContainer>
          ) : <p className="text-muted-foreground text-sm">Sem dados.</p>}
        </div>
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading font-bold text-lg mb-4">Saídas por Tipo</h3>
          {saidasData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={saidasData}><CartesianGrid strokeDasharray="3 3" stroke="hsl(210,10%,87%)" /><XAxis dataKey="name" tick={{ fontSize: 9 }} /><YAxis allowDecimals={false} /><Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{saidasData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-muted-foreground text-sm">Sem dados.</p>}
        </div>
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading font-bold text-lg mb-4">Entradas por Tipo</h3>
          {entradasData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={entradasData}><CartesianGrid strokeDasharray="3 3" stroke="hsl(210,10%,87%)" /><XAxis dataKey="name" tick={{ fontSize: 9 }} /><YAxis allowDecimals={false} /><Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{entradasData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-muted-foreground text-sm">Sem dados.</p>}
        </div>
      </div>

      {/* Monthly */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg">Relatório Mensal</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => { if (mes === 1) { setMes(12); setAno(ano - 1); } else setMes(mes - 1); }} className="p-1.5 rounded-full hover:bg-muted"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium w-24 text-center">{MONTHS[mes - 1]} {ano}</span>
            <button onClick={() => { if (mes === 12) { setMes(1); setAno(ano + 1); } else setMes(mes + 1); }} className="p-1.5 rounded-full hover:bg-muted"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        {mensal && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <MiniStat label="Entradas Total" value={mensal.entradas?.total} sub={`Cães: ${mensal.entradas?.caes || 0} | Gatos: ${mensal.entradas?.gatos || 0}`} />
            <MiniStat label="Adoções (Cães)" value={mensal.saidas?.adocao_caes || 0} />
            <MiniStat label="Adoções (Gatos)" value={mensal.saidas?.adocao_gatos || 0} />
            <MiniStat label="Morte Nat. (Cães)" value={mensal.saidas?.morte_natural_caes || 0} />
            <MiniStat label="Morte Nat. (Gatos)" value={mensal.saidas?.morte_natural_gatos || 0} />
            <MiniStat label="Eutanásia (Cães)" value={mensal.saidas?.eutanasia_caes || 0} />
            <MiniStat label="Eutanásia (Gatos)" value={mensal.saidas?.eutanasia_gatos || 0} />
            <MiniStat label="Levantamentos" value={mensal.saidas?.levantamentos || 0} />
            <MiniStat label="Saídas Total" value={mensal.saidas?.total || 0} />
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground text-center mt-4 print:mt-2">AnimaLog — Gerado em {new Date().toLocaleDateString('pt-PT')}</p>
    </div>
  );
}

function StatCard({ label, value, sub, color = 'text-foreground' }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-4">
      <p className={`text-2xl font-heading font-black ${color}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="bg-muted/30 rounded-xl p-3">
      <p className="text-lg font-heading font-bold text-foreground">{value || 0}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
